import React from 'react';
import { Box, Typography } from '@heycater/design-system';
import { Asset } from 'contentful';
import styled from 'styled-components';

import BackgroundImage from 'shared/components/BackgroundImage';
import EmbeddedFunnel from 'shared/components/EmbeddedFunnel';
import { getRating } from 'shared/services/trustedShops/handleRatings';
import { Ratings } from 'shared/services/trustedShops/model';
import RatingsComponent from 'static_pages/marketing_pages/dynamic_landing_page/components/Hero/Ratings';
import TrustedCustomers from 'static_pages/marketing_pages/dynamic_landing_page/components/Hero/TrustedCustomers';

const FALLBACK_IMAGE = '/artifacts/images/marketing_pages/catering.webp';

interface Props {
  title: string;
  term?: string;
  headerImage: Asset;
  ratings: Ratings;
}

function Hero({ title, headerImage, ratings }: Props) {
  const backgroundImage = headerImage?.fields?.file
    ? `https:${headerImage.fields?.file?.url}?w=1440`
    : FALLBACK_IMAGE;

  return (
    <StyledHero>
      <BackgroundImage
        src={backgroundImage}
        layout="fill"
        priority
        objectPosition="50% 74%"
        objectFit="cover"
        quality={70}
      />
      <HeroMainContent>
        <Box width="100%" color="#ffffff">
          <TypographyTitle variant="h1" component="h1">
            {title}
          </TypographyTitle>
        </Box>
      </HeroMainContent>
      <Box mb={1}>
        <EmbeddedFunnel
          renderTitle={(title) => (
            <Subtitle component="h2" variant="subtitle2">
              {title}&nbsp;
            </Subtitle>
          )}
          slides={{
            customerTier: { whiteWrapper: true },
          }}
          ratings={ratings}
          highlightColor="primary"
          onBackground="dark"
        />
      </Box>
      <HeroFooter>
        <TrustedCustomers />
      </HeroFooter>
      <RatingsWrapper>
        <RatingsComponent rating={getRating(ratings)} />
      </RatingsWrapper>
    </StyledHero>
  );
}

const Subtitle = styled(Typography)`
  font-weight: 600;
  font-size: 16px;
  white-space: pre-line;
  line-height: 150%;

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 18px;
  }
`;

const TypographyTitle = styled(Typography)`
  font-weight: 700;
  font-size: 36px;
  line-height: 119%;
  margin-bottom: ${({ theme }) => theme.spacing(2)}px;

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 38px;
    margin-bottom: ${({ theme }) => theme.spacing(2)}px;
  }
`;

const RatingsWrapper = styled.div`
  position: relative;
  display: block;
  background-color: white;

  ${({ theme }) => theme.breakpoints.up('lg')} {
    display: block;
    position: absolute;
    border-radius: 20px;
    overflow: hidden;

    background-color: white;
    right: 47px;
    bottom: 38px;
    width: fit-content;
  }
`;

const StyledHero = styled.div`
  position: relative;
  background: radial-gradient(
    124.78% 92.5% at 93.04% 116.94%,
    rgba(22, 29, 23, 0.8) 0%,
    rgba(85, 116, 71, 0.8) 100%
  );
  mix-blend-mode: normal;
  display: flex;
  flex-direction: column;

  overflow: hidden;
  text-align: center;

  ${({ theme }) => theme.breakpoints.up('md')} {
    min-height: 600px;
  }

  ${(props) => props.theme.breakpoints.down('sm')} {
    padding: ${(props) => props.theme.spacing(0)}px;
  }
`;

const HeroMainContent = styled.div`
  display: flex;
  flex-grow: 1;
  width: 100%;
  height: 100%;
  align-self: center;
  align-items: center;
  flex-direction: column;
  padding: ${(props) => props.theme.spacing(4, 3, 0, 3)};
  padding-left: 2rem;
  padding-right: 2rem;

  ${({ theme }) => theme.breakpoints.up('md')} {
    padding: ${(props) => props.theme.spacing(4, 2, 0, 2)};
  }
`;

const HeroFooter = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing(3)}px;
  flex-grow: 0;
  height: 100%;
`;

export default Hero;
